// src/views/pagamentos/PagamentosFila.jsx
// Módulo "Pagamentos": fila centralizada + pagamento/agendamento em lote +
// visão de calendário. Pensado para o financeiro executar dezenas de baixas
// rapidamente, sem precisar abrir cada conta a pagar individualmente.
import React, { useState, useEffect, useCallback, useMemo } from "react";
import pagamentosService from "../../services/pagamentosService";
import fornecedoresService from "../../services/fornecedoresService";
import contasBancariasService from "../../services/contasBancariasService";
import { useAuth } from "../../contexts/AuthContext";
import LotePagamentoModal from "./LotePagamentoModal";
import Banner from "../../components/ui/Banner";
import ConfirmModal from "../../components/ui/ConfirmModal";

const fmtMoeda = v => parseFloat(v||0).toLocaleString("pt-BR",{style:"currency",currency:"BRL"});
const fmtDt    = dt => { if(!dt) return "—"; try { return new Date(String(dt).split("T")[0]+"T12:00:00").toLocaleDateString("pt-BR"); } catch { return dt; } };

// Cores semânticas por situação da parcela
const statusBadge = (p) => {
  const dias = parseInt(p.diasAtraso||0);
  if (p.status === "Agendado") return { label:`Agendado ${fmtDt(p.dtPagamentoProgramado)}`, bg:"#E0F2FE", color:"#075985" };
  if (p.status === "Pago")     return { label:"Pago",     bg:"#DCFCE7", color:"#166534" };
  if (dias > 0)                return { label:`Vencido ${dias}d`, bg:"#FEE2E2", color:"#991B1B" };
  if (dias === 0)              return { label:"Vence hoje", bg:"#FFF7ED", color:"#C2410C" };
  return                        { label:`Vence em ${-dias}d`, bg:"#EFF6FF", color:"#1D4ED8" };
};

// ── Heatmap simples por dia (calendário mensal) ──────────────────────────
function CalendarioMes({ ano, mes, dados, onClickDia, onPrev, onNext }) {
  const primeiroDia = new Date(ano, mes, 1);
  const ultimoDia   = new Date(ano, mes+1, 0);
  const diaSemana   = primeiroDia.getDay(); // 0=domingo
  const totalDias   = ultimoDia.getDate();
  const cells = [];
  for (let i=0; i<diaSemana; i++) cells.push(null);
  for (let d=1; d<=totalDias; d++) cells.push(d);

  const dadosMap = Object.fromEntries((dados||[]).map(x => [x.dia, x]));
  const mesNome = primeiroDia.toLocaleDateString("pt-BR", { month:"long", year:"numeric" });

  return (
    <div style={{background:"var(--bg-surface)",border:"1px solid var(--gray-200)",borderRadius:10,padding:16}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <button className="btn btn-outline-secondary btn-sm" onClick={onPrev}><i className="bi bi-chevron-left"/></button>
        <span style={{fontWeight:700,fontSize:14,color:"var(--gray-700)",textTransform:"capitalize"}}>{mesNome}</span>
        <button className="btn btn-outline-secondary btn-sm" onClick={onNext}><i className="bi bi-chevron-right"/></button>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4}}>
        {["D","S","T","Q","Q","S","S"].map((d,i)=>(
          <div key={i} style={{fontSize:11,fontWeight:600,color:"var(--gray-400)",
            textAlign:"center",padding:"4px 0"}}>{d}</div>
        ))}
        {cells.map((dia, idx) => {
          if (!dia) return <div key={`v${idx}`}/>;
          const dt  = `${ano}-${String(mes+1).padStart(2,"0")}-${String(dia).padStart(2,"0")}`;
          const d   = dadosMap[dt];
          const tem = d?.qtd > 0;
          const hasVencido = d?.vencido > 0;
          const hasAgendado = d?.agendado > 0;
          const bg = !tem ? "#fff"
                   : hasVencido ? "#FEE2E2"
                   : hasAgendado ? "#E0F2FE"
                   : "#FFF7ED";
          const color = !tem ? "var(--gray-400)"
                      : hasVencido ? "#991B1B"
                      : hasAgendado ? "#075985"
                      : "#C2410C";
          return (
            <div key={dt} onClick={()=>tem && onClickDia?.(dt, d)}
              style={{minHeight:64, padding:6, border:"1px solid var(--gray-100)",
                borderRadius:6, background:bg, cursor:tem?"pointer":"default",
                transition:"transform .12s"}}
              onMouseEnter={e=>{ if(tem) e.currentTarget.style.transform="scale(1.04)"; }}
              onMouseLeave={e=>{ e.currentTarget.style.transform="scale(1)"; }}>
              <div style={{fontSize:12,fontWeight:700,color, marginBottom:2}}>{dia}</div>
              {tem && (
                <>
                  <div style={{fontSize:10,color,fontWeight:600}}>{d.qtd} parc.</div>
                  <div style={{fontSize:10,color,fontWeight:700}}>{fmtMoeda(d.total).replace("R$","").trim()}</div>
                </>
              )}
            </div>
          );
        })}
      </div>
      <div style={{display:"flex",gap:14,marginTop:12,fontSize:11,color:"var(--gray-500)"}}>
        <span><span style={{display:"inline-block",width:10,height:10,background:"var(--bg-danger-soft)",borderRadius:2,marginRight:4}}/>Vencidas</span>
        <span><span style={{display:"inline-block",width:10,height:10,background:"#FFF7ED",borderRadius:2,marginRight:4}}/>A vencer</span>
        <span><span style={{display:"inline-block",width:10,height:10,background:"#E0F2FE",borderRadius:2,marginRight:4}}/>Agendado</span>
      </div>
    </div>
  );
}

export default function PagamentosFila() {
  const { condominioAtivo, podeEditar } = useAuth();
  const canEdit = podeEditar("contasPagar");
  const idCondo = condominioAtivo?.id;

  const [aba, setAba] = useState("fila");           // fila | calendario | aprovacoes
  const [parcelas, setParcelas] = useState([]);
  const [totais, setTotais]     = useState({});
  const [loading, setLoading]   = useState(false);

  const [filtros, setFiltros] = useState({
    janela:"30d", status:"", idFornecedor:"", idContaBancaria:"",
    aprovacao:"aprovadas", agrupar:"none",
  });

  const [selecionadas, setSelecionadas] = useState(new Set());

  const [fornecedores, setFornecedores] = useState([]);
  const [contasBancarias, setContasBancarias] = useState([]);

  const [showLote, setShowLote] = useState(false);
  const [modoLote, setModoLote] = useState("lote");
  const [saving, setSaving]     = useState(false);

  // Calendário
  const [calData, setCalData] = useState([]);
  const hoje = useMemo(()=>new Date(),[]);
  const [calRef, setCalRef]   = useState({ ano:hoje.getFullYear(), mes:hoje.getMonth() });

  // Banner global de feedback
  const [banner, setBanner] = useState(null);
  const showBanner = (tipo, msg) => { setBanner({ tipo, msg }); setTimeout(()=>setBanner(null), 7000); };

  // Confirmação de cancelamento de agendamento
  const [cancelAgendId, setCancelAgendId] = useState(null);

  // ── Carregamentos ──────────────────────────────────────────────────────
  const carregar = useCallback(async () => {
    if (!idCondo) return;
    setLoading(true);
    try {
      const r = await pagamentosService.fila({ idCondominio: idCondo, ...filtros });
      setParcelas(r.data||[]);
      setTotais(r.totais||{});
      setSelecionadas(new Set()); // limpa seleção ao recarregar
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [idCondo, filtros]);

  useEffect(()=>{ carregar(); }, [carregar]);

  // Carrega listas auxiliares uma vez
  useEffect(() => {
    if (!idCondo) return;
    fornecedoresService.listar({ idCondominio:idCondo, situacao:"ativo", limit:300 })
      .then(r => setFornecedores(r.data||[])).catch(()=>{});
    contasBancariasService.listar({ idCondominio:idCondo, situacao:"ativo" })
      .then(r => setContasBancarias(r.data||[])).catch(()=>{});
  }, [idCondo]);

  // Calendário: carrega a faixa do mês de referência
  useEffect(() => {
    if (!idCondo || aba !== "calendario") return;
    const dtIni = `${calRef.ano}-${String(calRef.mes+1).padStart(2,"0")}-01`;
    const dtFim = new Date(calRef.ano, calRef.mes+1, 0).toISOString().split("T")[0];
    pagamentosService.calendario({ idCondominio:idCondo, dtIni, dtFim })
      .then(r => setCalData(r.data||[]))
      .catch(()=>setCalData([]));
  }, [idCondo, aba, calRef]);

  // ── Agrupamento da listagem ─────────────────────────────────────────────
  const grupos = useMemo(() => {
    if (filtros.agrupar === "none") return [{ key:"_", titulo:"", parcelas, subtotal: parcelas.reduce((a,p)=>a+parseFloat(p.vlrParcela||0),0) }];
    const map = {};
    for (const p of parcelas) {
      let key, titulo;
      if (filtros.agrupar === "fornecedor") {
        key = p.nmFornecedor || "—";
        titulo = key;
      } else if (filtros.agrupar === "vencimento") {
        key = String(p.dtVencimento).substring(0,7); // YYYY-MM
        titulo = new Date(key+"-01T12:00:00").toLocaleDateString("pt-BR",{month:"long",year:"numeric"});
      } else if (filtros.agrupar === "conta") {
        key = p.nmContaBancaria || "Sem conta definida";
        titulo = key;
      }
      if (!map[key]) map[key] = { key, titulo, parcelas:[], subtotal:0 };
      map[key].parcelas.push(p);
      map[key].subtotal += parseFloat(p.vlrParcela||0);
    }
    return Object.values(map).sort((a,b)=>a.titulo.localeCompare(b.titulo));
  }, [parcelas, filtros.agrupar]);

  // ── Seleção ────────────────────────────────────────────────────────────
  const toggleParcela = (id) => {
    setSelecionadas(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleTodasDoGrupo = (grupo) => {
    const ids = grupo.parcelas.map(p => p.id);
    const todasSelecionadas = ids.every(id => selecionadas.has(id));
    setSelecionadas(prev => {
      const next = new Set(prev);
      if (todasSelecionadas) ids.forEach(id => next.delete(id));
      else                   ids.forEach(id => next.add(id));
      return next;
    });
  };
  const toggleTodas = () => {
    const ids = parcelas.map(p => p.id);
    const todas = ids.every(id => selecionadas.has(id));
    setSelecionadas(todas ? new Set() : new Set(ids));
  };

  const parcelasSelecionadas = useMemo(
    () => parcelas.filter(p => selecionadas.has(p.id) && p.status !== "Agendado"),
    [parcelas, selecionadas]
  );
  const totalSelecionado = useMemo(
    () => parcelasSelecionadas.reduce((a,p)=>a+parseFloat(p.vlrParcela||0), 0),
    [parcelasSelecionadas]
  );

  // ── Confirmar lote/agendar ──────────────────────────────────────────────
  const handleConfirmarLote = async (form) => {
    setSaving(true);
    try {
      const r = modoLote === "agendar"
        ? await pagamentosService.agendar(form)
        : await pagamentosService.pagarLote(form);
      showBanner("success", r.message);
      setShowLote(false);
      await carregar();
    } catch(e) {
      const erros = e?.body?.erros || e?.erros;
      if (erros?.length) {
        showBanner("error", "Algumas parcelas não puderam ser processadas: " + erros.map(x=>x.motivo).join(" | "));
      } else {
        showBanner("error", e.message || "Erro ao processar pagamento em lote.");
      }
    }
    setSaving(false);
  };

  // ── Cancelar agendamento (confirma via modal) ──────────────────────────
  const confirmarCancelarAgendamento = async () => {
    if (!cancelAgendId) return;
    try {
      await pagamentosService.cancelarAgendamento([cancelAgendId]);
      setCancelAgendId(null);
      showBanner("success", "Agendamento cancelado.");
      await carregar();
    } catch(e) {
      setCancelAgendId(null);
      showBanner("error", e.message || "Erro ao cancelar agendamento.");
    }
  };

  const setF = (f, v) => setFiltros(p => ({ ...p, [f]: v }));

  if (!idCondo) return (
    <div style={{textAlign:"center",padding:60,color:"var(--gray-400)"}}>
      <i className="bi bi-buildings" style={{fontSize:40,display:"block",marginBottom:12}}/>
      Selecione um condomínio para ver os pagamentos
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">
            <i className="bi bi-cash-coin me-2" style={{color:"var(--primary)",fontSize:22}}/>
            Pagamentos
          </h1>
          <p className="page-subtitle">
            {condominioAtivo?.nmFantasia && <><strong>{condominioAtivo.nmFantasia}</strong> — </>}
            Fila centralizada, agendamento e baixa em lote
          </p>
        </div>
        <button className="btn btn-outline-secondary btn-sm" onClick={carregar} disabled={loading}>
          <i className={`bi bi-arrow-clockwise ${loading?"spin":""}`}/>
        </button>
      </div>

      {banner && (
        <Banner tipo={banner.tipo} onClose={()=>setBanner(null)}>
          {banner.msg}
        </Banner>
      )}

      {/* KPIs */}
      <div className="row g-3 mb-4">
        {[
          { icon:"bi-exclamation-triangle-fill", cls:"red",    val:fmtMoeda(totais.totalVencido),   label:`Vencidas (${totais.qtdVencidas||0})`,   color:"#991B1B" },
          { icon:"bi-calendar-event-fill",        cls:"orange", val:fmtMoeda(totais.totalHoje),       label:`Vence hoje (${totais.qtdHoje||0})`,      color:"#C2410C" },
          { icon:"bi-clock-fill",                 cls:"blue",   val:fmtMoeda(totais.totalAVencer),    label:`A vencer (${totais.qtdAVencer||0})`,    color:"#1D4ED8" },
          { icon:"bi-calendar-check-fill",        cls:"cyan",   val:fmtMoeda(totais.totalAgendado),   label:`Agendadas (${totais.qtdAgendadas||0})`, color:"#0891B2" },
        ].map(s => (
          <div className="col-6 col-md-3" key={s.label}>
            <div style={{background:"var(--bg-surface)",border:"1px solid var(--gray-200)",borderRadius:10,
              padding:"14px 16px", borderLeft:`4px solid ${s.color}`}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <div style={{width:40,height:40,borderRadius:10,background:s.color+"22",
                  display:"flex",alignItems:"center",justifyContent:"center"}}>
                  <i className={`bi ${s.icon}`} style={{fontSize:18,color:s.color}}/>
                </div>
                <div>
                  <div style={{fontSize:16,fontWeight:700,color:"var(--gray-800)"}}>{s.val}</div>
                  <div style={{fontSize:11.5,color:"var(--gray-500)",marginTop:2}}>{s.label}</div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="card">
        {/* Abas */}
        <div style={{borderBottom:"1px solid var(--gray-200)",padding:"0 16px",display:"flex",gap:4}}>
          {[
            { id:"fila",       label:"Fila de Pagamentos", icon:"bi-list-check" },
            { id:"calendario", label:"Calendário",          icon:"bi-calendar3" },
          ].map(t => (
            <button key={t.id} onClick={()=>setAba(t.id)}
              style={{fontSize:13,padding:"12px 14px",background:"transparent",border:"none",cursor:"pointer",
                borderBottom: aba===t.id?"2px solid var(--primary)":"2px solid transparent",
                fontWeight: aba===t.id?600:400,
                color: aba===t.id?"var(--primary)":"var(--gray-500)",
                display:"flex",alignItems:"center",gap:6}}>
              <i className={`bi ${t.icon}`}/>{t.label}
            </button>
          ))}
        </div>

        {/* ── FILA ── */}
        {aba==="fila" && (
          <>
            {/* Filtros */}
            <div style={{padding:"12px 16px",borderBottom:"1px solid var(--gray-100)",
              display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
              {[
                { v:"vencidas", l:"Vencidas",     color:"#991B1B" },
                { v:"hoje",     l:"Vence hoje",   color:"#C2410C" },
                { v:"7d",       l:"Próximos 7d",  color:"#1D4ED8" },
                { v:"15d",      l:"Próximos 15d", color:"#1D4ED8" },
                { v:"30d",      l:"Próximos 30d", color:"#1D4ED8" },
                { v:"",         l:"Todas",        color:"var(--gray-500)" },
              ].map(j => (
                <button key={j.v||"all"} onClick={()=>setF("janela", j.v)}
                  style={{padding:"5px 12px",borderRadius:6,
                    border:`1px solid ${filtros.janela===j.v?j.color:"#E2E8F0"}`,
                    background: filtros.janela===j.v ? j.color : "#fff",
                    color: filtros.janela===j.v ? "#fff" : j.color,
                    fontWeight:600, fontSize:12, cursor:"pointer"}}>
                  {j.l}
                </button>
              ))}

              <div style={{height:24,width:1,background:"var(--gray-200)",margin:"0 4px"}}/>

              <select className="form-select form-select-sm" style={{width:"auto"}}
                value={filtros.idFornecedor} onChange={e=>setF("idFornecedor",e.target.value)}>
                <option value="">Todos fornecedores</option>
                {fornecedores.map(f => <option key={f.id} value={f.id}>{f.nmReduzido||f.nmRazaoSocial}</option>)}
              </select>

              <select className="form-select form-select-sm" style={{width:"auto"}}
                value={filtros.idContaBancaria} onChange={e=>setF("idContaBancaria",e.target.value)}>
                <option value="">Todas contas bancárias</option>
                {contasBancarias.map(c => <option key={c.id} value={c.id}>{c.nmConta}</option>)}
              </select>

              <select className="form-select form-select-sm" style={{width:"auto"}}
                value={filtros.status} onChange={e=>setF("status",e.target.value)}>
                <option value="">Aberto + Vencido + Agendado</option>
                <option value="Aberto">Em aberto</option>
                <option value="Vencido">Vencido</option>
                <option value="Agendado">Agendado</option>
                <option value="Pago">Pago (auditoria)</option>
              </select>

              <select className="form-select form-select-sm" style={{width:"auto"}}
                value={filtros.agrupar} onChange={e=>setF("agrupar",e.target.value)}>
                <option value="none">Sem agrupamento</option>
                <option value="fornecedor">Agrupar por fornecedor</option>
                <option value="vencimento">Agrupar por mês de vencimento</option>
                <option value="conta">Agrupar por conta bancária</option>
              </select>

              <select className="form-select form-select-sm" style={{width:"auto"}}
                value={filtros.aprovacao} onChange={e=>setF("aprovacao",e.target.value)}>
                <option value="aprovadas">Só aprovadas</option>
                <option value="pendentes">Aguarda aprovação</option>
                <option value="">Todas</option>
              </select>
            </div>

            {/* Barra de seleção/ações em lote (fixa quando há seleção) */}
            {selecionadas.size > 0 && (
              <div style={{padding:"10px 16px",background:"#1B3A6B",color:"#fff",
                display:"flex",justifyContent:"space-between",alignItems:"center",
                position:"sticky",top:0,zIndex:5}}>
                <div style={{fontSize:13}}>
                  <i className="bi bi-check2-square me-2"/>
                  <strong>{parcelasSelecionadas.length}</strong> parcela(s) selecionada(s) —
                  Total: <strong>{fmtMoeda(totalSelecionado)}</strong>
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button className="btn btn-sm btn-outline-light"
                    onClick={()=>setSelecionadas(new Set())}>
                    <i className="bi bi-x-circle me-1"/>Limpar
                  </button>
                  <button className="btn btn-sm btn-info text-white"
                    disabled={!canEdit || parcelasSelecionadas.length===0}
                    onClick={()=>{ setModoLote("agendar"); setShowLote(true); }}>
                    <i className="bi bi-calendar-plus me-1"/>Agendar
                  </button>
                  <button className="btn btn-sm btn-success"
                    disabled={!canEdit || parcelasSelecionadas.length===0}
                    onClick={()=>{ setModoLote("lote"); setShowLote(true); }}>
                    <i className="bi bi-cash-coin me-1"/>Pagar em lote
                  </button>
                </div>
              </div>
            )}

            {/* Tabela */}
            <div className="table-responsive">
              <table className="table table-custom" style={{margin:0}}>
                <thead>
                  <tr>
                    <th style={{width:36}}>
                      <input type="checkbox"
                        checked={parcelas.length>0 && parcelas.every(p=>selecionadas.has(p.id))}
                        onChange={toggleTodas}/>
                    </th>
                    <th>Fornecedor</th>
                    <th>Descrição</th>
                    <th>Vencimento</th>
                    <th>Situação</th>
                    <th>Conta Bancária</th>
                    <th style={{textAlign:"right"}}>Valor</th>
                    <th style={{width:60}}/>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={8} className="text-center py-5">
                      <span className="spinner-border spinner-border-sm text-primary me-2"/>
                    </td></tr>
                  ) : parcelas.length === 0 ? (
                    <tr><td colSpan={8} className="text-center py-5" style={{color:"var(--gray-400)"}}>
                      <i className="bi bi-check-circle" style={{fontSize:36,display:"block",marginBottom:8,color:"#166534"}}/>
                      Nenhuma parcela na fila para os filtros selecionados
                    </td></tr>
                  ) : grupos.map(g => (
                    <React.Fragment key={g.key}>
                      {filtros.agrupar !== "none" && (
                        <tr style={{background:"var(--bg-highlight)"}}>
                          <td>
                            <input type="checkbox"
                              checked={g.parcelas.every(p=>selecionadas.has(p.id))}
                              onChange={()=>toggleTodasDoGrupo(g)}/>
                          </td>
                          <td colSpan={5} style={{fontWeight:700,color:"var(--primary)",fontSize:13}}>
                            {g.titulo} <span style={{color:"var(--gray-500)",fontWeight:400}}>
                              · {g.parcelas.length} parc.</span>
                          </td>
                          <td style={{textAlign:"right",fontWeight:700,color:"var(--primary)"}}>{fmtMoeda(g.subtotal)}</td>
                          <td/>
                        </tr>
                      )}
                      {g.parcelas.map(p => {
                        const badge = statusBadge(p);
                        const isAgendado = p.status === "Agendado";
                        return (
                          <tr key={p.id} style={{background: selecionadas.has(p.id) ? "#F0F9FF" : "transparent"}}>
                            <td>
                              <input type="checkbox"
                                checked={selecionadas.has(p.id)}
                                onChange={()=>toggleParcela(p.id)}/>
                            </td>
                            <td>
                              <div style={{fontWeight:600,fontSize:13}}>{p.nmFornecedor||"—"}</div>
                              <div style={{fontSize:11,color:"var(--gray-400)"}}>{p.cnpjFornecedor||""}</div>
                            </td>
                            <td style={{fontSize:13, maxWidth:260, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
                              {p.descricao}
                              <div style={{fontSize:11,color:"var(--gray-400)"}}>
                                {p.tpDocumento}{p.nrDocumento?` ${p.nrDocumento}`:""} · Parc. {p.nrParcela}/{p.nrParcelasTotal}
                              </div>
                            </td>
                            <td style={{fontSize:13, whiteSpace:"nowrap"}}>{fmtDt(p.dtVencimento)}</td>
                            <td>
                              <span style={{background:badge.bg, color:badge.color,
                                padding:"3px 10px", borderRadius:12, fontSize:11, fontWeight:700}}>
                                {badge.label}
                              </span>
                              {p.statusAprovacao === "Pendente" && (
                                <div style={{fontSize:10,color:"#854D0E",marginTop:3,fontWeight:600}}>
                                  <i className="bi bi-hourglass me-1"/>Aguarda aprovação
                                </div>
                              )}
                            </td>
                            <td style={{fontSize:12,color:"var(--gray-500)"}}>{p.nmContaBancaria||"—"}</td>
                            <td style={{textAlign:"right",fontWeight:700,fontSize:13}}>{fmtMoeda(p.vlrParcela)}</td>
                            <td style={{textAlign:"center"}}>
                              {isAgendado && (
                                <button className="btn btn-outline-secondary btn-sm btn-icon"
                                  title="Cancelar agendamento"
                                  onClick={()=>setCancelAgendId(p.id)}>
                                  <i className="bi bi-calendar-x"/>
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* ── CALENDÁRIO ── */}
        {aba==="calendario" && (
          <div style={{padding:16}}>
            <CalendarioMes
              ano={calRef.ano} mes={calRef.mes}
              dados={calData}
              onPrev={()=>setCalRef(r => {
                const d = new Date(r.ano, r.mes-1, 1);
                return { ano:d.getFullYear(), mes:d.getMonth() };
              })}
              onNext={()=>setCalRef(r => {
                const d = new Date(r.ano, r.mes+1, 1);
                return { ano:d.getFullYear(), mes:d.getMonth() };
              })}
              onClickDia={(dt) => {
                // Filtra a fila pelo dia clicado
                setFiltros(p => ({...p, janela:"", dtIni:dt, dtFim:dt}));
                setAba("fila");
              }}
            />
            <div style={{fontSize:12,color:"var(--gray-500)",marginTop:10,textAlign:"center"}}>
              Clique em um dia para ver as parcelas correspondentes na fila
            </div>
          </div>
        )}
      </div>

      <LotePagamentoModal
        show={showLote}
        modo={modoLote}
        onClose={()=>setShowLote(false)}
        parcelas={parcelasSelecionadas}
        saving={saving}
        idCondominio={idCondo}
        onConfirmar={handleConfirmarLote}
      />

      {cancelAgendId && (
        <ConfirmModal
          titulo="Cancelar agendamento"
          mensagem="Esta parcela voltará ao status 'Aberto' e o agendamento será descartado. Deseja continuar?"
          textoOk="Cancelar agendamento"
          textoCancelar="Voltar"
          perigo
          onConfirm={confirmarCancelarAgendamento}
          onCancel={()=>setCancelAgendId(null)}
        />
      )}

      <style>{`
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        .spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
}
